import * as THREE from 'three';
import * as CANNON from 'cannon-es';

export function cannonShapeToGeometry(shape) {
  if (!shape) return new THREE.BoxGeometry(1, 1, 1);

  if (shape instanceof CANNON.Box) {
    const he = shape.halfExtents;
    return new THREE.BoxGeometry(he.x * 2, he.y * 2, he.z * 2);
  }

  if (shape instanceof CANNON.Sphere) {
    return new THREE.SphereGeometry(shape.radius, 24, 18);
  }

  if (shape instanceof CANNON.Cylinder) {
    const rTop = shape.radiusTop || shape.radius || 0.5;
    const rBottom = shape.radiusBottom || shape.radius || rTop;
    const height = shape.height || shape.length || 1;
    return new THREE.CylinderGeometry(rTop, rBottom, height, 16);
  }

  if (shape instanceof CANNON.Plane) {
    return new THREE.PlaneGeometry(10, 10);
  }

  if (shape instanceof CANNON.ConvexPolyhedron) {
    const verts = shape.vertices || [];
    const faces = shape.faces || [];
    const bufGeo = new THREE.BufferGeometry();
    const positions = [];

    for (let f = 0; f < faces.length; f++) {
      const face = faces[f];
      if (face.length >= 3) {
        for (let k = 1; k < face.length - 1; k++) {
          const a = verts[face[0]];
          const b = verts[face[k]];
          const c = verts[face[k + 1]];
          positions.push(a.x, a.y, a.z);
          positions.push(b.x, b.y, b.z);
          positions.push(c.x, c.y, c.z);
        }
      }
    }

    if (positions.length > 0) {
      bufGeo.setAttribute(
        'position',
        new THREE.Float32BufferAttribute(positions, 3)
      );
      bufGeo.computeVertexNormals();
      return bufGeo;
    }
  }

  return new THREE.BoxGeometry(1, 1, 1);
}

export function createColliderMaterial() {
  return new THREE.MeshBasicMaterial({
    color: 0x00ffff,
    wireframe: true,
    transparent: true,
    opacity: 0.85,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
}

/**
 * Local-space center of the visual bounds relative to the object origin (pivot).
 * After Edit Pivot Point, geometry is offset from the origin — colliders must
 * use this center so they wrap the mesh, not the pivot gizmo.
 */
export function computeGeometryCenterOffset(mesh) {
  if (!mesh) return null;

  try {
    const worldScale = new THREE.Vector3(1, 1, 1);
    try {
      mesh.updateMatrixWorld?.(true);
      mesh.matrixWorld?.decompose?.(
        new THREE.Vector3(),
        new THREE.Quaternion(),
        worldScale
      );
    } catch (_) {
      worldScale.set(
        mesh.scale?.x ?? 1,
        mesh.scale?.y ?? 1,
        mesh.scale?.z ?? 1
      );
    }

    // Fast path: single mesh with geometry (shapes, images, videos, text).
    if (mesh.isMesh && mesh.geometry) {
      mesh.geometry.computeBoundingBox();
      const bb = mesh.geometry.boundingBox;
      if (bb && !bb.isEmpty()) {
        const center = bb.getCenter(new THREE.Vector3());
        center.x *= worldScale.x;
        center.y *= worldScale.y;
        center.z *= worldScale.z;
        if (center.lengthSq() < 1e-12) return null;
        return { x: center.x, y: center.y, z: center.z };
      }
    }

    // Groups / multi-mesh (GLB pivots): bounds at identity transform + world scale.
    let hasAudio = false;
    mesh.traverse?.((o) => {
      if (o?.type === 'PositionalAudio' || o?.type === 'Audio') hasAudio = true;
    });

    const box3 = new THREE.Box3();
    if (!hasAudio) {
      try {
        const clone = mesh.clone(true);
        clone.position.set(0, 0, 0);
        clone.quaternion.identity();
        clone.scale.copy(worldScale);
        box3.setFromObject(clone);
      } catch (_) {
        box3.setFromObject(mesh);
        const origin = new THREE.Vector3();
        mesh.getWorldPosition(origin);
        box3.min.sub(origin);
        box3.max.sub(origin);
      }
    } else {
      return null;
    }

    if (box3.isEmpty()) return null;
    const center = box3.getCenter(new THREE.Vector3());
    if (center.lengthSq() < 1e-12) return null;
    return { x: center.x, y: center.y, z: center.z };
  } catch (_) {
    return null;
  }
}

export function isPivotEditedMesh(mesh) {
  return !!(
    mesh?.userData?.pivotEdited ||
    mesh?.pivotEdited ||
    mesh?.userData?.native_pivot ||
    mesh?.native_pivot
  );
}

export function buildColliderVisualFromShape(shape, material, offset = null) {
  const geom = cannonShapeToGeometry(shape);
  const mesh = new THREE.Mesh(geom, material);
  if (offset) {
    mesh.position.set(offset.x || 0, offset.y || 0, offset.z || 0);
  }
  return mesh;
}

export function getColliderViewSignature(physicsProperties, mesh) {
  const com = physicsProperties?.centerOfMass || { x: 0, y: 0, z: 0 };
  const automatic =
    physicsProperties?.automaticCenterOfMass !== false &&
    physicsProperties?.automaticCenterOfMass !== 0;
  const scale = mesh?.scale || { x: 1, y: 1, z: 1 };
  // Automatic Center → mass/collider origin at pivot (local 0,0,0).
  const centerSig = automatic
    ? 'pivot'
    : `${com.x},${com.y},${com.z}`;
  return [
    physicsProperties?.colliderType || 'Box',
    centerSig,
    `${scale.x},${scale.y},${scale.z}`,
    mesh?.userData?.pivotEdited ? 'pivot' : 'nopivot',
  ].join('|');
}

/**
 * Shape offset in object-local space relative to the pivot.
 * Automatic Center → null (mass at pivot).
 * Manual → Center Of Mass XYZ from the pivot.
 */
export function resolveCenterOfMassOffset(physicsProperties, mesh = null) {
  if (
    physicsProperties?.automaticCenterOfMass !== false &&
    physicsProperties?.automaticCenterOfMass !== 0
  ) {
    return null;
  }

  const com =
    physicsProperties?.centerOfMass ||
    physicsProperties?.centerOfMassOffset ||
    null;
  if (com && (com.x || com.y || com.z)) {
    return { x: com.x || 0, y: com.y || 0, z: com.z || 0 };
  }
  return null;
}

export function findSceneMesh(scene, objectId) {
  if (!scene || !objectId) return null;

  let mesh = scene.getObjectByName(objectId);
  if (mesh) return mesh;

  scene.traverse((child) => {
    if (
      !mesh &&
      (child.name === objectId ||
        child._id === objectId ||
        child.url === objectId ||
        child.userData?.id === objectId ||
        child.userData?._id === objectId ||
        child.userData?.temp_file_name === objectId)
    ) {
      mesh = child;
    }
  });

  return mesh;
}
